import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'outline-white';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center rounded-md font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

    const variants = {
      primary: 'bg-outfront-purple text-white hover:bg-outfront-purple-dark active:bg-outfront-purple-dark focus-visible:ring-outfront-purple',
      secondary: 'bg-outfront-charcoal text-white hover:bg-neutral-600 active:bg-neutral-900 focus-visible:ring-outfront-charcoal',
      outline:
        'border border-neutral-200 bg-white text-outfront-charcoal hover:bg-neutral-50 hover:border-outfront-purple hover:text-outfront-purple active:bg-neutral-100 focus-visible:ring-outfront-purple',
      ghost: 'text-outfront-charcoal hover:bg-neutral-100 hover:text-outfront-purple active:bg-neutral-200 focus-visible:ring-outfront-purple',
      danger: 'bg-status-negative text-white hover:bg-red-800 active:bg-red-900 focus-visible:ring-status-negative',
      'outline-white': 'border border-white bg-transparent text-white hover:bg-white hover:text-outfront-purple focus-visible:ring-white',
    };

    const sizes = {
      sm: 'h-8 px-3 text-sm',
      md: 'h-10 px-4 text-base',
      lg: 'h-12 px-6 text-lg',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
export default Button;
